// One lane model, two sources.
//
// The room's canonical state and a seed's uploaded Snapshot describe the same
// thing in different shapes: the DO keeps flat `tracks` + `clips` joined by
// trackId, while a Snapshot nests clips inside each track. Normalising both to
// this shape is what lets a single ArrangementView render a live room and a
// stored version without either side leaking into the other.

import { renderStatus } from "@shared/sync/clip-render.js";
import { CENTER_PAN, UNITY_VOLUME } from "@shared/mixer.js";
import type { WireNote } from "@shared/midi/notes.js";
import type { CanonicalClipState, CanonicalTrackState, Snapshot } from "@web-shared/lib/types";
import type { AudioClipInfo, SessionAudioClipInfo } from "@shared/seed-history.js";
import { seedClipGeometry } from "@shared/playback/geometry.js";
import { type WarpSettingsData } from "@shared/sync/protocol.js";

export interface LaneClip {
  id: string;
  name: string;
  start: number;
  duration: number;
  color: number;
  muted: boolean;
  /** Present only for live-room clips — the R2 key backing the audio. */
  audioKey?: string | null;
  rev?: number;
  filePath?: string;
  /** The clip carries browser-only warp that hosts without a warp engine
   * (Live, Audiotool) get as a render: "pending" while it is made, or when
   * it is older than the clip; absent ⇒ nothing to render, or "ready". */
  render?: "pending" | "ready";
  /** Absent ⇒ audio. A MIDI clip's `audioKey` is an optional bounce, so its
   * absence is not "missing audio". */
  kind?: "audio" | "midi";
  /** MIDI clips: how many notes the clip holds. */
  noteCount?: number;
  /** MIDI clips: the flattened note list (clip-relative beats), when the wire
   * carried it. Absent on over-cap clips (`notesOmitted`) and on old rooms. */
  notes?: WireNote[];
  /** Session View clips only: the clip slot (scene index). A slot clip has
   * no timeline position — `start` is 0 and it lives in `Lane.sessionClips`,
   * never in `Lane.clips`. */
  /** MIDI slot clips: Live's Loop switch (protocol.ts `slotLooping`).
   * Absent ⇒ unknown, and a launch loops. */
  slotLooping?: boolean;
  slot?: number;
  /** A slot clip INFERRED from the timeline rather than put there by the
   * producer (lib/ghost-clips.ts). Player-side only: nothing ghosted is ever
   * proposed to a room or drawn on the timeline it came from. */
  ghost?: true;
  /** Audio clips: the markers Live plays by. Absent on MIDI (flattened on
   * the wire) and on lanes built from older data. */
  geometry?: ClipGeometry;
}

/** A clip's playback markers, in clip-internal beats. Object time 0 is
 * `startMarker` (a region render starts where the clip does). */
export interface ClipGeometry {
  isWarped: boolean;
  looping: boolean;
  startMarker: number;
  endMarker: number;
  loopStart: number;
  loopEnd: number;
  /** Warped clips: the tempo the markers are measured at (slice 48 —
   * `ClipData.sourceTempo`). Absent ⇒ unknown, the object plays at its
   * own speed. */
  sourceTempo?: number;
  /** Live's warp mode value (`ClipData.warpModeValue`); Re-Pitch (3)
   * changes pitch with speed, every other mode stretches. */
  warpMode?: number;
  /** Live's warp markers for the object (slice 49): the clip's beat ↔ file
   * second. Two or more and the browser plays the file onto the grid
   * through them, `sourceTempo` only carrying it past the last. */
  warpMarkers?: Array<{ beat: number; sec: number }>;
  /** Clip Transpose, semitones (`ClipData.transpose`). Absent ⇒ 0. */
  transpose?: number;
  /** Clip Detune, cents (`ClipData.detune`). Absent ⇒ 0. */
  detune?: number;
  /** The warp-mode knobs (`ClipData.warpSettings`). Absent ⇒ Live's defaults. */
  warpSettings?: WarpSettingsData;
}

export interface Lane {
  id: string;
  name: string;
  mute: boolean;
  solo: boolean;
  arm: boolean;
  /** Mixer fader position, 0..1 (unity ≈0.85 = 0 dB — shared/mixer.ts). */
  volume: number;
  /** Mixer pan position, -1..1 (0 = center — shared/mixer.ts). */
  pan: number;
  rev?: number;
  /** Timeline (arrangement) clips, in startTime order. */
  clips: LaneClip[];
  /** Session View clips, by slot. Empty for lanes with none. */
  sessionClips: LaneClip[];
  /** Absent ⇒ audio. */
  kind?: "audio" | "midi";
  /** A MIDI lane's instrument (shared/instruments.ts id); absent ⇒ sine. */
  instrument?: string;
}

export interface Arrangement {
  lanes: Lane[];
  /** Rightmost clip end, in beats. 0 when there is nothing to draw. */
  length: number;
  /**
   * Scene rows of the Session View: the snapshot's scenes where recorded,
   * grown to cover the highest occupied slot. 0 when nothing sits in a slot
   * and no scenes were recorded — the grid then has nothing to draw.
   */
  sceneCount: number;
  /** Scene names by row, where the snapshot recorded them ("" otherwise). */
  sceneNames: string[];
}

function finish(lanes: Lane[], sceneNames: ReadonlyArray<string | null> = []): Arrangement {
  const length = lanes.reduce(
    (max, lane) => lane.clips.reduce((m, c) => Math.max(m, c.start + c.duration), max),
    0,
  );
  const highestSlot = lanes.reduce(
    (max, lane) => lane.sessionClips.reduce((m, c) => Math.max(m, c.slot ?? -1), max),
    -1,
  );
  const sceneCount = Math.max(sceneNames.length, highestSlot + 1);
  return {
    lanes,
    length,
    sceneCount,
    sceneNames: Array.from({ length: sceneCount }, (_, i) => sceneNames[i] ?? ""),
  };
}

/** Whether anything at all sits in the Session View. */
export function hasSessionClips(arrangement: Arrangement): boolean {
  return arrangement.lanes.some((l) => l.sessionClips.length > 0);
}

/**
 * A room audio clip's playback markers. Exported because the clip library
 * builds sources from the room's flat clip rows rather than from lanes, and
 * the card and the grid must not disagree about what a clip plays.
 */
export function roomClipGeometry(c: CanonicalClipState): ClipGeometry {
  return {
    isWarped: c.isWarped,
    looping: c.looping,
    startMarker: c.startMarker,
    endMarker: c.endMarker,
    loopStart: c.loopStart,
    loopEnd: c.loopEnd,
    ...(c.sourceTempo !== undefined ? { sourceTempo: c.sourceTempo } : {}),
    ...(c.warpMarkers && c.warpMarkers.length >= 2 ? { warpMarkers: c.warpMarkers } : {}),
    warpMode: c.warpModeValue,
    ...(c.transpose ? { transpose: c.transpose } : {}),
    ...(c.detune ? { detune: c.detune } : {}),
    ...(c.warpSettings && Object.keys(c.warpSettings).length > 0 ? { warpSettings: c.warpSettings } : {}),
  };
}

export function fromRoom(
  tracks: CanonicalTrackState[],
  clips: CanonicalClipState[],
  sceneNames: ReadonlyArray<string | null> = [],
): Arrangement {
  const byTrack = new Map<string, LaneClip[]>();
  const sessionByTrack = new Map<string, LaneClip[]>();
  for (const c of clips) {
    const laneClip: LaneClip = {
      id: c.id,
      name: c.name,
      start: c.startTime,
      duration: c.duration,
      color: c.color,
      muted: c.muted,
      audioKey: c.audioKey,
      rev: c.rev,
      filePath: c.filePath,
      ...(c.clipKind === "midi"
        ? {
            kind: "midi" as const,
            noteCount: c.notes?.length ?? 0,
            notes: c.notes,
            ...(c.slotLooping !== undefined ? { slotLooping: c.slotLooping } : {}),
          }
        : { geometry: roomClipGeometry(c) }),
      ...(c.slot !== undefined ? { slot: c.slot } : {}),
      ...(c.clipKind !== "midi" && renderStatus(c) !== "none" ? { render: renderStatus(c) as "pending" | "ready" } : {}),
    };
    // A slot clip and a timeline clip are one flat list in the room; here
    // they part ways, because the timeline must never draw a slot clip at 0.
    const target = c.slot !== undefined ? sessionByTrack : byTrack;
    target.set(c.trackId, [...(target.get(c.trackId) ?? []), laneClip]);
  }
  // `ord` is display order only — never identity. See shared/sync/state.ts.
  const lanes = [...tracks]
    .sort((a, b) => a.ord - b.ord)
    .map<Lane>((t) => ({
      id: t.id,
      name: t.name,
      mute: t.mute,
      solo: t.solo,
      arm: t.arm,
      // Older rooms serialize without volume/pan — default to unity and
      // center, never to silence or a hard side.
      volume: t.volume ?? UNITY_VOLUME,
      pan: t.pan ?? CENTER_PAN,
      rev: t.rev,
      clips: (byTrack.get(t.id) ?? []).sort((a, b) => a.start - b.start),
      sessionClips: (sessionByTrack.get(t.id) ?? []).sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0)),
      ...(t.trackKind === "midi" ? { kind: "midi" as const } : {}),
      ...(t.trackKind === "midi" && t.instrument ? { instrument: t.instrument } : {}),
    }));
  return finish(lanes, [...sceneNames]);
}

/**
 * A Session View audio clip's playback markers, as the seed page must read
 * them: the object is the whole source file, so `startMarker` / the loop
 * brace / the warp map are the only description of what Live plays.
 *
 * `sourceTempo` is the SET's tempo, the same value the merge engine stamps on
 * a warped create that carries none (shared/sync/merge-engine.ts
 * `withSourceTempo`): a slot clip's beat markers are measured on the Set's
 * grid, and past the last warp marker that tempo is what carries the rest.
 * Only a warped clip gets one — an unwarped one-shot plays at its file's own
 * speed, as Live plays it.
 */
export function sessionGeometry(c: Omit<SessionAudioClipInfo, "slot">, tempo: number): ClipGeometry {
  // `slot: 0` only says "a Session clip" to the shared rule; the slot
  // itself is the caller's.
  return seedClipGeometry({ ...c, slot: 0 }, tempo);
}

/** A seed TIMELINE clip's playback markers: a Live region render plays
 * flat; a browser-saved source clip (`audioIs: "source"`) keeps its warp,
 * markers, tempo and pitch — the same rule hydrate reads it by, so the seed
 * page and a room opened from the seed never play it differently. */
export function timelineGeometry(c: AudioClipInfo, tempo: number): ClipGeometry {
  return seedClipGeometry(c, tempo);
}

/** Lane id → instrument, for every MIDI lane that names one — what the
 * player (`useArrangementPlayer`'s `instruments`) plays them with. */
export function laneInstruments(arrangement: Pick<Arrangement, "lanes">): Record<string, string> {
  const out: Record<string, string> = {};
  for (const lane of arrangement.lanes) if (lane.kind === "midi" && lane.instrument) out[lane.id] = lane.instrument;
  return out;
}

export function fromSnapshot(snapshot: Snapshot): Arrangement {
  const lanes = snapshot.audioTracks.map<Lane>((t, i) => ({
    id: `${t.name}#${i}`,
    name: t.name,
    mute: t.mute,
    solo: t.solo,
    arm: t.arm,
    volume: t.volume ?? UNITY_VOLUME,
    pan: t.panning ?? CENTER_PAN,
    clips: [...t.arrangementClips]
      .sort((a, b) => a.startTime - b.startTime)
      .map((c, j) => ({
        id: `${t.name}#${i}:${c.name}#${j}`,
        name: c.name,
        start: c.startTime,
        duration: c.duration,
        color: c.color,
        muted: c.muted,
        filePath: c.filePath,
        // Present only on seeds that ship per-clip audio; the lane's stem is
        // the fallback for everything older.
        audioKey: c.audioKey ?? null,
        geometry: timelineGeometry(c, snapshot.song.tempo),
      })),
    // Slot clips: their own object or nothing — a stem is a timeline render
    // and never covers the Session View.
    //
    // Geometry MATTERS here in a way it does not for a timeline clip. A
    // timeline clip's object is a flattened region render — the trim, the
    // loop and the warp are already in the bytes — but a slot clip's object
    // is its SOURCE FILE WHOLE (upload/clip-audio.ts: renderPreFxAudio is
    // arrangement-addressed, so a slot has nothing to render). Without these
    // markers the player has no way back to what Live plays: it starts at
    // second 0 of the file, never loops, and never stretches to the seed's
    // tempo — so every edit to a slot clip was inaudible on this page
    // (2026-09-16). Same block `fromRoom` builds, for the same reason.
    sessionClips: [...(t.sessionClips ?? [])]
      .sort((a, b) => a.slot - b.slot)
      .map((c) => ({
        id: `${t.name}#${i}:slot${c.slot}`,
        name: c.name,
        start: 0,
        duration: c.duration,
        color: c.color,
        muted: c.muted,
        filePath: c.filePath,
        audioKey: c.audioKey ?? null,
        slot: c.slot,
        geometry: sessionGeometry(c, snapshot.song.tempo),
      })),
  }));
  // MIDI lanes live in a parallel collection on the snapshot; they render
  // after the audio lanes, matching the engine's audio-first lane order.
  const midiLanes = (snapshot.midiTracks ?? []).map<Lane>((t, i) => ({
    id: `midi:${t.name}#${i}`,
    name: t.name,
    mute: t.mute,
    solo: t.solo,
    arm: t.arm,
    volume: t.volume ?? UNITY_VOLUME,
    pan: t.panning ?? CENTER_PAN,
    kind: "midi",
    ...(t.instrument ? { instrument: t.instrument } : {}),
    clips: [...t.arrangementClips]
      .sort((a, b) => a.startTime - b.startTime)
      .map((c, j) => ({
        id: `midi:${t.name}#${i}:${c.name}#${j}`,
        name: c.name,
        start: c.startTime,
        duration: c.duration,
        color: c.color,
        muted: c.muted,
        audioKey: c.audioKey ?? null,
        kind: "midi" as const,
        noteCount: c.noteCount,
        notes: c.notes,
      })),
    sessionClips: [...(t.sessionClips ?? [])]
      .sort((a, b) => a.slot - b.slot)
      .map((c) => ({
        id: `midi:${t.name}#${i}:slot${c.slot}`,
        name: c.name,
        start: 0,
        duration: c.duration,
        color: c.color,
        muted: c.muted,
        audioKey: c.audioKey ?? null,
        kind: "midi" as const,
        noteCount: c.noteCount,
        notes: c.notes,
        slot: c.slot,
      })),
  }));
  return finish(
    [...lanes, ...midiLanes],
    (snapshot.song.scenes ?? []).map((s) => s.name),
  );
}

/** Vibrant lane palette — assigned per track when clips have no real color. */
const LANE_PALETTE = [
  "#FF6B6B", "#FFA94D", "#FFD43B", "#69DB7C", "#38D9A9",
  "#4DABF7", "#748FFC", "#DA77F2", "#F783AC", "#63E6BE",
  "#74C0FC", "#A9E34B", "#E599F7", "#66D9E8", "#FFC078",
];

/** True when a packed RGB is black, near-black, or a neutral gray —
 * colors that disappear on a dark UI and carry no musical meaning. */
function isGrayish(rgb: number): boolean {
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  // Max channel difference ≤ 30 means near-neutral, and brightness < 160
  // means it's dark enough to vanish on the dark background.
  const maxC = Math.max(r, g, b);
  const minC = Math.min(r, g, b);
  return (maxC - minC) <= 30 && maxC < 160;
}

/** Live stores colours as a packed int; CSS needs the low 24 bits as hex.
 * Gray, near-gray and black clips get a vibrant palette colour keyed by
 * the lane index so every track is visually distinct. */
export function clipColor(color: number, laneIndex?: number): string {
  const rgb = color & 0xffffff;
  if (laneIndex !== undefined && isGrayish(rgb)) return LANE_PALETTE[laneIndex % LANE_PALETTE.length]!;
  return `#${rgb.toString(16).padStart(6, "0")}`;
}
